import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Box, Title, Text } from '@nerdwallet/currency';
// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';
// eslint-disable-next-line lodash/import-scope
import get from 'lodash/get';
import { connect } from 'react-redux';
import Image from '@nerdwallet/react-image';
import { createStructuredSelector } from 'reselect';
import YouTubePlayer from '../image-content/youtube-player';
import styles from './hero-landscape-video.module.less';
import {
  stringInputSpec,
  stringSelectSpec,
  stringTextAreaSpec,
  boolCheckboxSpec,
  numberInputSpec,
} from '../../lib/editing-configs';
import BackgroundColor from '../background-color/background-color';
import getTextColor from '../../lib/text-color-config';
import parseMarkdownLinks from '../../lib/parse-md-links';

const backgroundSizes = [
  { label: 'Contain', value: 'contain' },
  { label: 'Cover', value: 'cover' },
  { label: 'Auto', value: 'auto' },
];

const backgroundColorPositionDefaultValue = 50;
const backgroundColorHightDefaultValue = 50;

const linkParser = (content) => parseMarkdownLinks(content, false);

const htmlOutputParser = (content, enableHtml = false) => {
  if (enableHtml) {
    // eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration
    return <span dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return linkParser(content);
};

const buildBackground = (
  showPartialBackgroundColor,
  viewPortSize,
  bgColor,
  bgImageUrl,
  bgColorPosition,
  bgHeight,
  horizontalAlignment,
  verticalAlignment,
  bgImagePosition
) => {
  if (showPartialBackgroundColor && viewPortSize === 'lg') {
    const bgEmptySpace = '#fff';
    let background = {};
    const bgGradient = `
    linear-gradient(0deg,
      ${bgEmptySpace} 0%,
      ${bgEmptySpace} ${Number(bgColorPosition)}%,
      ${bgColor} ${Number(bgColorPosition)}%,
      ${bgColor} ${Number(bgColorPosition) + Number(bgHeight)}%,
      ${bgEmptySpace} ${Number(bgColorPosition) + Number(bgHeight)}%,
      ${bgEmptySpace} 100%)
    `;

    if (bgColor && bgImageUrl) {
      // the order matters, otherwise it won't work
      background = `url(${bgImageUrl}) no-repeat ${horizontalAlignment} ${verticalAlignment} / ${bgImagePosition}, ${bgGradient}`;
      return { background };
    }

    if (bgColor && !bgImageUrl) {
      background = bgColor;
      return { background };
    }

    if (!bgColor && bgImageUrl) {
      // the order matters, otherwise it won't work
      background = `url(${bgImageUrl}) no-repeat ${horizontalAlignment} ${verticalAlignment} / ${bgImagePosition}`;
      return { background };
    }

    return { background };
  }

  return {
    background: `url(${bgImageUrl}) no-repeat ${horizontalAlignment} ${verticalAlignment}, ${bgColor}`,
  };
};

const HeroLandscapeVideo = ({
  title,
  titleWidth,
  titleMobileWidth,
  titleTabletWidth,
  titleMobile,
  titleSize,
  titleColorOptions,
  titleChronicle,
  bodyText,
  dangerouslyAllowHTMLInBody,
  bodyTextColorOptions,
  videoPlaceholderImageSize,
  videoPlaceHolderUrl,
  customPlayButtonImageUrl,
  bgOptions,
  backgroundImageSrcDesktop,
  backgroundImageSrcTablet,
  backgroundImageSrcMobile,
  backgroundSize,
  desktopAlignment,
  mobileAlignment,
  size,
  backgroundColorPosition,
  backgroundColorHight,
  overflowAnimatedImage,
  youtubeVideoSlug,
  showPartialBackgroundColor,
  enableModestbranding,
}) => {
  const lastChanceImageSrcMap = (screenSize) => {
    // return desktop background if mobile/tablet is not provided
    const type = {
      lg: backgroundImageSrcDesktop,
      md: backgroundImageSrcTablet,
      sm: backgroundImageSrcMobile,
    };
    if (type[screenSize]) {
      return type[screenSize];
    }
    return backgroundImageSrcDesktop;
  };

  const { textColor: titleTextColor } = getTextColor(titleColorOptions);
  const titleColorStyle = { color: titleTextColor };

  const { textColor: bodyTextTextColor } = getTextColor(bodyTextColorOptions);
  const bodyTextColorStyle = { color: bodyTextTextColor };

  function responsiveTitleWidth() {
    if (size === 'md') {
      return titleTabletWidth;
    }
    if (size === 'sm') {
      return titleMobileWidth;
    }
    return titleWidth;
  }

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => {
        return (
          <React.Fragment>
            <div
              className={styles.backgroundContainer}
              style={buildBackground(
                showPartialBackgroundColor,
                size,
                backgroundColor,
                lastChanceImageSrcMap(size),
                backgroundColorPosition || backgroundColorPositionDefaultValue,
                backgroundColorHight || backgroundColorHightDefaultValue,
                desktopAlignment,
                mobileAlignment,
                backgroundSize
              )}
            >
              <Box justifyContent="center" className={styles.videoContainer}>
                <Title
                  size={titleSize}
                  align="center"
                  style={{
                    ...titleColorStyle,
                    width: responsiveTitleWidth(),
                  }}
                  className={classNames([
                    styles.title,
                    {
                      [styles.inChronicle]: titleChronicle,
                    },
                  ])}
                >
                  {(size !== 'lg' && titleMobile) || title}
                </Title>

                <Box className={styles.videoBox}>
                  <YouTubePlayer
                    defaultPlayButtonIcon="white"
                    imagePlaceholderCoverSize={videoPlaceholderImageSize}
                    width="100%"
                    height="100%"
                    videoSlug={youtubeVideoSlug}
                    imagePlaceholder={videoPlaceHolderUrl}
                    playButtonImage={customPlayButtonImageUrl}
                    enableModestbranding={enableModestbranding}
                  />
                </Box>
              </Box>
            </div>
            {overflowAnimatedImage && (
              <Box className={styles.overflowImageBox}>
                <Image
                  naturalHeight={112}
                  naturalWidth={161}
                  backgroundColor="transparent"
                  className={styles.overflowImage}
                  src={overflowAnimatedImage}
                  alt="big star"
                />
              </Box>
            )}
            <Box className={styles.bodyTextBox} style={bodyTextColorStyle}>
              <Text>
                {htmlOutputParser(bodyText, dangerouslyAllowHTMLInBody)}
              </Text>
            </Box>
          </React.Fragment>
        );
      }}
    </BackgroundColor>
  );
};

HeroLandscapeVideo.displayName = 'Hero Landscape Video';

HeroLandscapeVideo.propTypes = {
  title: PropTypes.string,
  titleWidth: PropTypes.string,
  titleMobile: PropTypes.string,
  titleMobileWidth: PropTypes.string,
  titleTabletWidth: PropTypes.string,
  titleChronicle: PropTypes.bool,
  ...BackgroundColor.propTypes,
  videoPlaceholderImageSize: PropTypes.string,
  videoPlaceHolderUrl: PropTypes.string,
  customPlayButtonImageUrl: PropTypes.string,
  backgroundImageSrcDesktop: PropTypes.string,
  backgroundImageSrcTablet: PropTypes.string,
  backgroundImageSrcMobile: PropTypes.string,
  backgroundSize: PropTypes.oneOf(['contain', 'cover', 'auto']),
  backgroundColorPosition: PropTypes.number,
  backgroundColorHight: PropTypes.number,
  bodyText: PropTypes.string,
  dangerouslyAllowHTMLInBody: PropTypes.bool,
  showPartialBackgroundColorPosition: PropTypes.string,
  overflowAnimatedImage: PropTypes.string,
  youtubeVideoSlug: PropTypes.string,
  enableModestbranding: PropTypes.bool,
};

HeroLandscapeVideo.defaultProps = {
  title: '',
  titleWidth: '100%',
  titleMobile: '',
  titleMobileWidth: '100%',
  titleTabletWidth: '100%',
  titleChronicle: false,
  videoPlaceholderImageSize: 'contain',
  videoPlaceHolderUrl: '',
  customPlayButtonImageUrl: '',
  backgroundImageSrcDesktop: '',
  backgroundImageSrcTablet: '',
  backgroundImageSrcMobile: '',
  backgroundSize: 'contain',
  bodyText: '',
  dangerouslyAllowHTMLInBody: false,
  backgroundColorPosition: backgroundColorHightDefaultValue,
  backgroundColorHight: backgroundColorHightDefaultValue,
  desktopAlignment: 'center',
  mobileAlignment: 'center',
  overflowAnimatedImage: '',
  youtubeVideoSlug: '',
  enableModestbranding: false,
};

HeroLandscapeVideo.getEditingConfig = (componentDocument) => {
  const showPartialBackgroundColorInput =
    get(componentDocument, 'props.showPartialBackgroundColor', false) === true
      ? [
          numberInputSpec(
            'backgroundColorPosition',
            'Show Partial Background Color Position (numbers only)',
            {
              help: 'Percentage to be filled with background color from bottom to top, default to 50%',
            }
          ),
          numberInputSpec(
            'backgroundColorHight',
            'Show Partial Background Color Hight (numbers only)',
            {
              help: 'Percentage height to be filled with background color, from bottom to top default to 50',
            }
          ),
        ]
      : [];

  return [
    stringInputSpec('title', 'Title'),
    stringInputSpec('titleWidth', 'Title Width', {
      help: 'In pixels or Percentage. Defaults to 100%',
    }),
    stringInputSpec('titleTabletWidth', 'Title Tablet Width', {
      help: 'In pixels or Percentage. Defaults to 100%',
    }),
    stringInputSpec('titleMobile', 'Title Mobile'),
    stringInputSpec('titleMobileWidth', 'Title Mobile Width', {
      help: 'In pixels or Percentage. Defaults to 100%',
    }),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'titleColorOptions',
      'Title Color'
    ),
    stringSelectSpec(
      `titleSize`,
      'Title Size',
      [
        { label: 'Default', value: 'default' },
        { label: 'Small', value: 'small' },
      ],
      {
        help: 'Defaults to small',
      }
    ),
    boolCheckboxSpec('titleChronicle', 'Title in Chronicle?'),
    stringInputSpec('videoPlaceHolderUrl', 'Video Placeholder Image URL'),
    boolCheckboxSpec('enableModestbranding', 'Enable Modestbranding?', {
      help: 'If checked the player will not show a YouTube logo',
    }),
    stringInputSpec('youtubeVideoSlug', 'Video Slug', {
      help: 'The Video Slug is the string of text/numerals that immediately follows the https://youtu.be/ portion of the Share URL generated by YouTube.',
    }),
    stringSelectSpec(
      'videoPlaceholderImageSize',
      'Video Placeholder Image Size',
      backgroundSizes
    ),
    stringInputSpec('customPlayButtonImageUrl', 'Custom Play Button Image URL'),
    ...BackgroundColor.getEditingConfig(componentDocument),
    boolCheckboxSpec(
      'showPartialBackgroundColor',
      'Show partial background color?',
      {
        help: 'If checked enables background color partial display',
      }
    ),
    ...showPartialBackgroundColorInput,
    stringInputSpec(
      'backgroundImageSrcDesktop',
      'Background Image URL (Desktop)'
    ),
    stringInputSpec(
      'backgroundImageSrcTablet',
      'Background Image URL (Tablet)'
    ),
    stringInputSpec(
      'backgroundImageSrcMobile',
      'Background Image URL (Mobile)'
    ),
    stringSelectSpec('backgroundSize', 'Background Size', backgroundSizes, {
      help: 'Only applies to Desktop and Tablet',
    }),
    stringSelectSpec(
      'desktopAlignment',
      'Desktop Alignment',
      [
        { label: 'Top', value: 'top' },
        { label: 'Right', value: 'right' },
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Bottom', value: 'bottom' },
      ],
      {
        help: 'Only applies to Desktop and Tablet',
      }
    ),
    stringSelectSpec(
      'mobileAlignment',
      'Mobile Alignment',
      [
        { label: 'Top', value: 'top' },
        { label: 'Right', value: 'right' },
        { label: 'Left', value: 'left' },
        { label: 'Middle', value: 'center' },
        { label: 'Bottom', value: 'bottom' },
      ],
      {
        help: 'Only applies to Mobile',
      }
    ),
    stringTextAreaSpec('bodyText', 'Body Text'),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'bodyTextColorOptions',
      'Body Text Color'
    ),
    boolCheckboxSpec('dangerouslyAllowHTMLInBody', 'Allow HTML in body?', {
      help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
    }),
    stringInputSpec('overflowAnimatedImage', 'Overflow Animated Image'),
  ];
};

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(HeroLandscapeVideo);
